import { useMemo } from "react";
import type { Node, Edge } from "reactflow";
import {
  REVEAL_AREA_STAGGER_MS,
  REVEAL_TOPIC_STAGGER_MS,
  REVEAL_EDGE_OFFSET_MS,
} from "./constants";

/**
 * Computes reveal delay for each node and edge based on topological order.
 * Returns new node/edge arrays with `revealDelay` injected into `data`.
 */
export function useGraphRevealAnimation(
  nodes: Node[],
  edges: Edge[],
  isAnimating: boolean,
): { nodes: Node[]; edges: Edge[] } {
  return useMemo(() => {
    if (!isAnimating || !nodes.length) {
      return {
        nodes: nodes.map((n) => ({
          ...n,
          data: { ...n.data, revealDelay: 0 },
        })),
        edges: edges.map((e) => ({
          ...e,
          data: { ...e.data, revealDelay: 0 },
        })),
      };
    }

    const areaNodes = nodes.filter((n) => n.type === "areaGroup");
    const groupNodes = nodes.filter((n) => n.type === "topicsGroup");

    // Sort by Y position (top to bottom)
    const sortedAreas = [...areaNodes].sort((a, b) => a.position.y - b.position.y);

    const delayMap = new Map<string, number>();

    // Stagger area nodes
    sortedAreas.forEach((node, i) => {
      delayMap.set(node.id, i * REVEAL_AREA_STAGGER_MS / 1000);
    });

    // Topics group appears shortly after its parent area
    for (const group of groupNodes) {
      const parentEdge = edges.find((e) => e.target === group.id && e.source.startsWith("area::"));
      const parentDelay = parentEdge ? (delayMap.get(parentEdge.source) ?? 0) : 0;
      delayMap.set(group.id, parentDelay + REVEAL_TOPIC_STAGGER_MS / 1000);
    }

    const animatedNodes = nodes.map((n) => ({
      ...n,
      data: { ...n.data, revealDelay: delayMap.get(n.id) ?? 0 },
    }));

    const animatedEdges = edges.map((e) => {
      const sourceDelay = delayMap.get(e.source) ?? 0;
      const targetDelay = delayMap.get(e.target) ?? 0;
      const edgeDelay = Math.max(sourceDelay, targetDelay) + REVEAL_EDGE_OFFSET_MS / 1000;
      return {
        ...e,
        data: { ...e.data, revealDelay: edgeDelay },
      };
    });

    return { nodes: animatedNodes, edges: animatedEdges };
  }, [nodes, edges, isAnimating]);
}
